import { useCallback, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ref, runTransaction, update } from "firebase/database";
import { LuBell, LuTrash2, LuCheck, LuX, LuUserPlus } from "react-icons/lu";
import { database } from "../constants/firebase";
import { useUser } from "../contexts/UserContext";
import { dateValue, rows, useRecord } from "../utils/appData";
import {
  friendshipPatch,
  displayName,
  profileNiches,
} from "../utils/discoverData";
import {
  notificationRoute,
  markNotificationsRead,
  deleteNotifications,
} from "../utils/notificationRoutes";
import Avatar from "../components/UserAvatar";
import AppPage, { State } from "../components/AppPage";
export { notificationRoute } from "../utils/notificationRoutes";
export default function NotificationCenter() {
  const { currentUser } = useUser();
  const uid = currentUser?.uid;
  const inbox = useRecord(uid ? `Notifications/${uid}` : null);
  const directory = useRecord(uid ? "Users" : null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState({});
  const [clearing, setClearing] = useState(false);
  const [marking, setMarking] = useState(false);
  const navigate = useNavigate();
  const list = useMemo(
    () =>
      rows(inbox.data).sort(
        (a, b) => dateValue(b.createdAt) - dateValue(a.createdAt),
      ),
    [inbox.data],
  );
  const me = directory.data?.[uid] || {};
  const requests = Object.entries(me.friendRequests || {})
    .filter(([id, status]) => status === "received" && !me.blockedUsers?.[id])
    .map(([id]) => ({ ...directory.data?.[id], id }))
    .filter((person) => directory.data?.[person.id]);
  const clearAll = useCallback(async () => {
    if (
      !uid ||
      !list.length ||
      !window.confirm(
        "Clear all notification history? Pending friend requests remain.",
      )
    )
      return;
    const ids = list.map((n) => n.id);
    setClearing(true);
    setError("");
    try {
      await runTransaction(ref(database, `Notifications/${uid}`), (current) => {
        return deleteNotifications(current, ids);
      });
    } catch (e) {
      setError(e.message);
    } finally {
      setClearing(false);
    }
  }, [uid, list]);
  const headerActions = useMemo(
    () => (
      <button
        disabled={clearing || !list.length}
        onClick={clearAll}
        className="rounded-full px-3 py-2 text-sm font-bold text-[#327691] disabled:opacity-40 dark:text-[#5FAFCB]"
      >
        {clearing ? "Clearing…" : "Clear all"}
      </button>
    ),
    [clearAll, clearing, list.length],
  );
  async function markAll() {
    if (!uid || marking) return;
    const ids = list.filter((n) => n.read !== true).map((n) => n.id);
    setMarking(true);
    setError("");
    try {
      await runTransaction(ref(database, `Notifications/${uid}`), (current) => {
        return markNotificationsRead(current, ids);
      });
    } catch (e) {
      setError(e.message);
    } finally {
      setMarking(false);
    }
  }
  async function open(n) {
    if (!uid) return;
    try {
      await runTransaction(
        ref(database, `Notifications/${uid}/${n.id}`),
        (current) => (current ? { ...current, read: true } : undefined),
      );
      const route = notificationRoute(n);
      if (route) navigate(route);
      else if (
        String(n.type || n.data?.type).toUpperCase() === "FRIEND_REQUEST"
      )
        document
          .getElementById("friend-requests")
          ?.scrollIntoView({ behavior: "smooth" });
    } catch (e) {
      setError(e.message);
    }
  }
  async function remove(n) {
    if (!uid || !window.confirm("Delete this notification?")) return;
    try {
      await update(ref(database, `Notifications/${uid}`), { [n.id]: null });
    } catch (e) {
      setError(e.message);
    }
  }
  async function answer(person, accept) {
    if (!uid || busy[person.id]) return;
    setBusy((prev) => ({ ...prev, [person.id]: true }));
    setError("");
    try {
      await update(
        ref(database),
        friendshipPatch(uid, person.id, accept ? "received" : "sent"),
      );
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy((prev) => ({ ...prev, [person.id]: false }));
    }
  }
  return (
    <AppPage title="Notifications" headerActions={headerActions}>
      {error && (
        <p
          role="alert"
          className="mb-4 rounded-xl bg-red-50 p-3 text-sm text-red-700 dark:bg-red-950 dark:text-red-200"
        >
          {error}
        </p>
      )}
      <State loading={directory.loading} error={directory.error} />
      {!!requests.length && (
        <section id="friend-requests" className="mb-6 scroll-mt-5">
          <h2 className="mb-3 flex items-center gap-2 text-[17px] font-bold">
            <LuUserPlus size={18} />
            Friend requests{" "}
            <span className="text-sm text-slate-500">({requests.length})</span>
          </h2>
          <div className="grid gap-3 md:grid-cols-1">
            {requests.map((person) => (
              <article
                key={person.id}
                className="flex min-w-0 items-center gap-3 rounded-2xl border border-black/5 bg-white p-3 dark:border-[#363e46] dark:bg-[#23272c]"
              >
                <Link
                  to={`/user-profile/${person.id}`}
                  className="flex min-w-0 flex-1 items-center gap-3"
                >
                  <Avatar
                    user={person}
                    className="h-11 w-11 shrink-0 rounded-full object-cover"
                  />
                  <span className="min-w-0">
                    <strong className="block truncate text-sm font-bold">
                      {displayName(person)}
                    </strong>
                    <small className="block truncate text-xs text-slate-500">
                      {profileNiches(person)[0] || person.niche || "Creator"}
                    </small>
                  </span>
                </Link>
                <div className="flex shrink-0 gap-2">
                  <button
                    onClick={() => answer(person, false)}
                    disabled={busy[person.id]}
                    aria-label={`Decline friend request from ${displayName(person)}`}
                    className="grid h-9 w-9 place-items-center rounded-full bg-red-50 text-red-600 disabled:opacity-40 dark:bg-red-950"
                  >
                    <LuX size={18} />
                  </button>
                  <button
                    onClick={() => answer(person, true)}
                    disabled={busy[person.id]}
                    aria-label={`Accept friend request from ${displayName(person)}`}
                    className="grid h-9 w-9 place-items-center rounded-full bg-[#327691] text-white disabled:opacity-40"
                  >
                    <LuCheck size={18} />
                  </button>
                </div>
              </article>
            ))}
          </div>
        </section>
      )}
      <section>
        <header className="mb-3 flex flex-wrap items-center justify-between gap-3">
          <h2 className="text-[17px] font-bold">Notification history</h2>
          <button
            disabled={marking || !list.some((n) => n.read !== true)}
            onClick={markAll}
            className="text-xs font-semibold text-[#327691] disabled:opacity-40 dark:text-[#5FAFCB]"
          >
            {marking ? "Marking…" : "Mark all as read"}
          </button>
        </header>
        <State {...inbox} empty={!list.length}>
          No notifications yet
        </State>
        <div className="grid gap-3">
          {list.map((n) => (
            <article
              key={n.id}
              className={`flex min-w-0 gap-2 rounded-2xl border p-3 ${n.read === true ? "border-black/5 bg-white dark:border-[#363e46] dark:bg-[#23272c]" : "border-[#cbdfe6] bg-[#e9f1f4] dark:border-[#33525c] dark:bg-[#22333a]"}`}
            >
              <button
                onClick={() => open(n)}
                className="min-w-0 flex-1 text-left"
              >
                <strong className="flex items-start gap-2 text-sm font-bold">
                  <LuBell className="mt-0.5 shrink-0" size={15} />
                  {n.title || n.notification?.title || "Zylen"}
                </strong>
                <p className="mt-2 whitespace-pre-wrap break-words text-[13px] leading-5">
                  {n.body || n.notification?.body || ""}
                </p>
                <time className="mt-2 block text-[11px] text-slate-500 dark:text-slate-400">
                  {dateValue(n.createdAt)
                    ? new Date(dateValue(n.createdAt)).toLocaleString()
                    : ""}
                </time>
                <small className="mt-1 block text-[10px] font-semibold text-[#327691] dark:text-[#5FAFCB]">
                  {n.read === true ? "Read" : "Unread"}
                </small>
              </button>
              <button
                onClick={() => remove(n)}
                aria-label={`Delete notification ${n.title || "Zylen"}`}
                className="grid h-9 w-8 shrink-0 place-items-center rounded-full text-slate-400 hover:text-red-500"
              >
                <LuTrash2 size={17} />
              </button>
            </article>
          ))}
        </div>
      </section>
    </AppPage>
  );
}
